import './style.scss'
import { Button, Drawer, Input, message, notification, Popconfirm, Progress, Space } from 'antd'
import { RcFile } from 'antd/es/upload'
import Dragger from 'antd/es/upload/Dragger'
import axios from 'axios'
import { FC, memo, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsApi } from '@/api'
import * as Api from '@/api/account'
import * as homeApi from '@/api/home'
import AudioDemos from '@/components/AudioDemos'
import SystemDeclare from '@/components/Declare'
import PayPointModal from '@/components/PayPointModal'
import { UserStore } from '@/global-states'
import { eventTracking, showDeclare, urlSource } from '@/libs/util'
import { CloudUploadOutlined, DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons'

interface IProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
  group?: any
  model?: number // 0-基础版 1-流畅版
}

const CloneDrawer: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['voice', 'system'])
  const { open, onCancel, onOk, group, model } = props
  const { declareChecked } = UserStore
  const [name, setName] = useState('')
  const [audioUrl, setAudioUrl] = useState<string>(undefined as any)
  const [audioInfo, setAudioInfo] = useState<any>({})
  const [percent, setPercent] = useState(-1)
  const [loading, setLoading] = useState(false)
  const cancelTokenSource = useRef<any>()
  const [clonePreflight, setClonePreflight] = useState<any>()
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [languageList, setLanguageList] = useState<any[]>([])
  const [selectLanguages, setSelectLanguages] = useState<any[]>([])

  useEffect(() => {
    if (open) {
      getClonePreflight()
      setName('')
      resetUploadAudio()
      setTitle('')
      getLanguageList()
    }
  }, [open])

  const getClonePreflight = async () => {
    const res = await Api.clonePreflight({
      level: 10
    })
    setClonePreflight(res || {})
  }

  const getLanguageList = async () => {
    const res = await homeApi.getLanguageCodes({
      tts_model: model
    })
    setLanguageList(res?.list || [])
    setSelectLanguages([res?.list?.[0].language_code])
  }

  const beforeAudioUpload = async (file: RcFile) => {
    return new Promise((resolve) => {
      const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
      if (['wav', 'mp3', 'm4a'].includes(type)) {
        const audioElement = document.createElement('audio')
        audioElement.src = URL.createObjectURL(file)
        audioElement.addEventListener('loadedmetadata', () => {
          console.log('videoElement', audioElement.duration)
          if (audioElement.duration < 5 || audioElement.duration > 180) {
            message.warning(translate('voice.audio_duration_warning'))
            resolve(false)
          }
          resolve(true)
        })
      } else {
        message.warning(translate('voice.audio_format_warning'))
        resolve(false)
      }
    })
  }

  const onDrop = async (file: RcFile) => {
    const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
    if (!['wav', 'mp3', 'm4a'].includes(type)) {
      message.warning(translate('voice.audio_format_warning'))
    }
  }

  const uploadFile = async (file: RcFile) => {
    try {
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type, public_url } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type: 2
        })) || {}
      if (!upload_url) {
        throw new Error(translate('system.upload_failed'))
      }

      cancelTokenSource.current = axios.CancelToken.source()

      setPercent(0)

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.progress || 0) * 100)
          setPercent(percent)
        },
        headers: {
          'Content-Type': content_type
        },
        cancelToken: cancelTokenSource.current?.token
      })
      setAudioInfo({
        title: segs[0],
        oss_key,
        public_url
      })
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        message.error(err?.message || err)
      }
    }
  }

  const resetUploadAudio = () => {
    if (audioInfo?.recommend) {
      UserStore.declareChecked = false
    }
    setPercent(-1)
    setAudioInfo({})
  }

  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.current?.cancel(translate('system.cancel_upload'))
      resetUploadAudio()
    }
  }

  const selectDemoAudio = (v: any) => {
    UserStore.declareChecked = true
    setPercent(100)
    setAudioUrl(urlSource(v.url))
    setAudioInfo({
      oss_key: v.ossKey,
      recommend: true
    })
  }

  const onConfirm = async () => {
    if (!declareChecked) {
      await showDeclare()
    }

    if (!name?.trim() && !group?.id) {
      return message.warning(translate('voice.input_voice_name'))
    }

    if (group?.id && !title) {
      return message.warning(translate('voice.input_style_name'))
    }

    if (!audioInfo.oss_key) {
      return message.warning(translate('voice.upload_reference_audio'))
    }

    eventTracking(model === 1 ? 'InstantCloneFishConfirm' : 'InstantCloneBasicConfirm', {
      ossKey: audioInfo.oss_key
    })

    setLoading(true)
    try {
      const params: any = {
        audio_oss_key: [audioInfo.oss_key],
        level: 10,
        with_group: {
          new_group_title: name
        },
        tts_model: model,
        language_codes: selectLanguages || []
      }

      if (group?.id) {
        params.voice_name = title
        params.with_group = {
          group_id: group.id
        }
      }
      await homeApi.voiceClone(params)
      notification.success({
        message: translate('voice.clone_in_progress'),
        description: translate('voice.clone_success_message')
      })
      onCancel?.()
      onOk?.()
    } catch (error: any) {
      if (error.code === 1002) {
        setPayPointModalOpen(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const onSelectLanguage = (code: string) => {
    if (selectLanguages.includes(code)) {
      if (selectLanguages.length > 1) {
        setSelectLanguages(selectLanguages.filter((s) => s !== code))
      }
    } else {
      setSelectLanguages([...selectLanguages, code])
    }
  }

  return (
    <Suspense>
      <Drawer
        className="create-drawer"
        title={model === 1 ? translate('voice.voice_clone_smooth') : translate('voice.voice_clone_basic')}
        width={720}
        onClose={onCancel}
        open={open}
        footer={
          <>
            <SystemDeclare />
            <Space>
              <div className="fee">
                <div className="score">
                  {clonePreflight?.limited_time_free && (
                    <label className="light gradient">{translate('voice.limited_time_free')}</label>
                  )}
                </div>
              </div>
              <Button onClick={onCancel}>{translate('system.cancel')}</Button>
              <Button type="primary" onClick={onConfirm} loading={loading}>
                {translate('system.confirm')}
              </Button>
            </Space>
          </>
        }
      >
        <div className="main">
          <div className="form-item">
            <div className="title">{translate('voice.voice_name')}</div>
            <Input
              maxLength={20}
              placeholder={translate('voice.input_voice_name')}
              value={group?.title || name}
              onChange={(e) => setName(e.target.value)}
              disabled={group?.id}
            />
          </div>

          {group?.id && (
            <div className="form-item">
              <div className="title">{translate('voice.style_name')}</div>
              <Input
                maxLength={20}
                placeholder={translate('voice.input_style_placeholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          )}
          <div className="form-item">
            <div className="title">
              {translate('voice.reference_audio')}
              <Popconfirm
                title={translate('system.tip')}
                description={
                  <div className="popconfirm-list" style={{ width: 620 }}>
                    <div>
                      （1）<strong>{translate('voice.audio_duration')}：</strong>
                      {translate('voice.audio_duration_tip_premium')}
                    </div>
                    <div>
                      （2）<strong>{translate('voice.audio_quality')}：</strong>
                    </div>
                    <ul>
                      <li>{translate('voice.audio_quality_tip1')}</li>
                      <li>{translate('voice.audio_quality_tip2')}</li>
                      <li>{translate('voice.audio_quality_tip3')}</li>
                      <li>{translate('voice.audio_quality_tip4')}</li>
                      <li>{translate('voice.audio_quality_tip5')}</li>
                      <li>{translate('voice.audio_quality_tip6')}</li>
                    </ul>
                    <div>
                      （3）<strong>{translate('voice.audio_content')}：</strong>
                    </div>
                    <ul>
                      <li>
                        {translate('voice.audio_content_tip1')}
                        <span className="red">{translate('voice.audio_content_tip2')}</span>
                      </li>
                    </ul>
                    <div>
                      （4）<strong>{translate('voice.audio_format')}：</strong>
                    </div>
                    <ul>
                      <li>{translate('voice.audio_format_tip')}</li>
                    </ul>
                  </div>
                }
                showCancel={false}
              >
                <ExclamationCircleFilled />
              </Popconfirm>
            </div>

            {percent === -1 && (
              <>
                <div className="warning">
                  <h4>{translate('voice.audio_requirements')}</h4>
                  <div className="desc">
                    <div>
                      <label className="label">{translate('voice.file_format')}：</label>
                      <label>{translate('voice.supported_formats')}</label>
                    </div>
                    <div>
                      <label className="label">{translate('voice.audio_duration')}：</label>
                      <label>{translate('voice.duration_range')}</label>
                    </div>
                  </div>
                </div>
                <Dragger
                  accept=".mp3,.m4a,.wav"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    const flag = (await beforeAudioUpload(file)) as any
                    if (flag) {
                      setAudioUrl(URL.createObjectURL(file))
                      uploadFile(file)
                    }
                    return flag
                  }}
                  onDrop={(e) => onDrop(e.dataTransfer.files?.[0] as any)}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">{translate('voice.upload_audio_prompt')}</p>
                  <p className="ant-upload-hint">{translate('system.upload_hint')}</p>
                </Dragger>
              </>
            )}

            {percent > -1 && percent < 100 && (
              <div className="step-progress">
                <div className="step-progress-content">
                  <div className="percent">{percent}%</div>
                  <Progress percent={percent} showInfo={false} />
                  <div className="tips">{translate('voice.audio_uploading')}</div>
                </div>
                <div className="btns">
                  <Button onClick={cancelUpload}>{translate('system.cancel_upload')}</Button>
                </div>
              </div>
            )}

            {percent === 100 && (
              <div className="step-view">
                <div className="step-view-box">
                  <audio controls src={audioUrl} />
                  <div className="trash" onClick={resetUploadAudio}>
                    <DeleteOutlined />
                  </div>
                </div>
              </div>
            )}

            {percent === 100 && (
              <div className="language-list">
                <div className="title">{translate('voice.select_language_prompt')}</div>
                <div className="list">
                  {languageList.map((l) => (
                    <div
                      key={l.language_code}
                      className={`list-item ${selectLanguages?.includes(l.language_code) ? 'actived' : ''}`}
                      onClick={onSelectLanguage.bind(this, l.language_code)}
                    >
                      {l.display_name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {percent === -1 && open && <AudioDemos onSelect={selectDemoAudio} />}
          </div>
        </div>
      </Drawer>

      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />
    </Suspense>
  )
}

export default memo(CloneDrawer)
